import { EventData } from '@/interfaces/event';
import { chatContentInfoAtom, chatCustomerAtom, chatCountAtom } from '@/stores/chat';
import im from '@/utils/im';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { ChatReceptionStatus, CustomerCount } from '@/interfaces/chat';

export default function useCustomerEventListener() {
  const [customer, setCustomer] = useAtom(chatCustomerAtom);
  const updateChatContentInfo = useSetAtom(chatContentInfoAtom);
  const setChatCount = useSetAtom(chatCountAtom);

  const currentCustomerId = customer?.customerId;
  const currentSubsourceId = customer?.subSourceId;

  // 更新接待类型计数
  const updateReceptionTypeCount = useCallback(
    (fromType: ChatReceptionStatus, toType: ChatReceptionStatus) => {
      if (fromType === toType) return;

      setChatCount((count: CustomerCount) => {
        const { receptionTypeCount, ...rest } = count;
        const newReceptionTypeCount = { ...receptionTypeCount };

        // 从原类型减1
        if (newReceptionTypeCount[fromType] > 0) {
          newReceptionTypeCount[fromType]--;
        }

        // 向新类型加1
        newReceptionTypeCount[toType] = (newReceptionTypeCount[toType] || 0) + 1;

        return { ...rest, receptionTypeCount: newReceptionTypeCount };
      });
    },
    [setChatCount],
  );

  const onCusChangeAiEnable = useCallback(
    (data: EventData) => {
      const { targetId, body } = data;
      const enableAi = body.enableAi;
      if (data.targetKind !== 'customer' || targetId !== currentCustomerId) {
        return;
      }
      setCustomer((prev) => {
        if (prev?.customerId !== targetId) return prev;
        // 根据 enableAi 更新 receptionType
        const oldReceptionType = prev.receptionType;
        const newReceptionType = enableAi ? 'ai_agent' : 'manual';

        // 更新计数
        updateReceptionTypeCount(oldReceptionType, newReceptionType);

        return { ...prev, enableAi, receptionType: newReceptionType };
      });
    },
    [currentCustomerId, setCustomer, updateReceptionTypeCount],
  );
  const onChannelChangeEnableAi = useCallback(
    (data: EventData) => {
      if (data.targetKind !== 'channel' || data.targetId !== currentSubsourceId) {
        return;
      }
      if (data.body.enableAi) {
        updateChatContentInfo({ customerId: currentCustomerId! });
      } else {
        setCustomer((prev) => (prev?.subSourceId ? { ...prev, enableAi: false } : prev));
      }
    },
    [currentCustomerId, currentSubsourceId, setCustomer, updateChatContentInfo],
  );

  const onCusAutoDisableAi = useCallback(
    (data: EventData) => {
      if (data.targetKind !== 'customer' || data.targetId !== currentCustomerId) {
        return;
      }
      setCustomer((prev) => {
        if (prev?.customerId !== currentCustomerId) return prev;
        // 只有在当前是小鼓AI接待时才切换到人工模式
        if (prev.receptionType === 'ai_agent') {
          // 更新计数
          updateReceptionTypeCount('ai_agent', 'manual');
          return { ...prev, enableAi: false, receptionType: 'manual' };
        }
        return prev;
      });
    },
    [currentCustomerId, setCustomer, updateReceptionTypeCount],
  );

  const onCusDelayAutoEnableAi = useCallback(
    (data: EventData) => {
      if (data.targetKind !== 'customer' || data.targetId !== currentCustomerId) {
        return;
      }
      setCustomer((prev) => {
        if (prev?.customerId !== currentCustomerId) return prev;
        // 只有在当前是人工接待时才切换到小鼓AI模式
        if (prev.receptionType === 'manual') {
          // 更新计数
          updateReceptionTypeCount('manual', 'ai_agent');
          return { ...prev, enableAi: true, receptionType: 'ai_agent' };
        }
        return prev;
      });
    },
    [currentCustomerId, setCustomer, updateReceptionTypeCount],
  );

  const onCusChangeEnableWorkflow = useCallback(
    (data: EventData) => {
      const { targetId, body } = data;
      const enableWorkflow = body.enableWorkflow;
      if (data.targetKind !== 'customer' || targetId !== currentCustomerId) {
        return;
      }
      setCustomer((prev) => {
        if (prev?.customerId !== targetId) return prev;
        // 根据 enableWorkflow 更新 receptionType
        const oldReceptionType = prev.receptionType;
        const newReceptionType = enableWorkflow ? 'workflow' : 'manual';

        // 更新计数
        updateReceptionTypeCount(oldReceptionType, newReceptionType);

        return { ...prev, receptionType: newReceptionType };
      });
    },
    [currentCustomerId, setCustomer, updateReceptionTypeCount],
  );

  useEffect(() => {
    im.on('cus_change_enable_ai', onCusChangeAiEnable);
    im.on('channel_change_enable_ai', onChannelChangeEnableAi);
    im.on('cus_auto_disable_ai', onCusAutoDisableAi);
    im.on('cus_delay_auto_enable_ai', onCusDelayAutoEnableAi);
    im.on('cus_change_enable_workflow', onCusChangeEnableWorkflow);
    return () => {
      im.off('cus_change_enable_ai', onCusChangeAiEnable);
      im.off('channel_change_enable_ai', onChannelChangeEnableAi);
      im.off('cus_auto_disable_ai', onCusAutoDisableAi);
      im.off('cus_delay_auto_enable_ai', onCusDelayAutoEnableAi);
      im.off('cus_change_enable_workflow', onCusChangeEnableWorkflow);
    };
  }, [
    onCusChangeAiEnable,
    onChannelChangeEnableAi,
    onCusAutoDisableAi,
    onCusDelayAutoEnableAi,
    onCusChangeEnableWorkflow,
  ]);
}
